import { ReactElement, useMemo, useCallback } from 'react';
import { Stack, Typography, Divider } from '@mui/material';
import { Cancel as CancelIcon, Edit as EditIcon } from '@mui/icons-material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faLayerGroup as SubscriptionsIcon,
  faHourglassStart as ActiveDateIcon,
  faCalendarTimes as ExpiredDateIcon,
  faArrowsSpin as CyleDateIcon,
  faCreditCard as CreditCardIcon,
  faReceipt as NextPaymentIcon,
  faCircleInfo as InfoIcon,
} from '@fortawesome/free-solid-svg-icons';
import { useSnackbar } from 'notistack';
import moment from 'moment';
import { isNil, upperCase } from 'lodash';

import { useToggle, useLazyRequest } from '~hooks/index';
import {
  cancelMPPaymentPlanSubscription,
  cancelPaypalPaymentPlanSubscription,
} from '~services/subscription/payment';
import { formatCurrencyValue, formatterDate } from '~utils/index';
import { isCancelledCurrentPlan } from '~utils/plans';
import { MenuActionsDataTable, DialogConfimation } from '~components/index';
import { PATHS } from '~constants/paths';
import {
  PaymentGatewayTypes,
  PaymentFrecuencyTypes,
} from '~globals/types/enums';

import SubscriptionEditPaymentModal from '../EditPaymentModal';
import SubscriptionPlanCardRowInfo from '../PlanCard/RowInfo';

import { SubscriptionPlanFlexCardProps } from './types';
import {
  PLAN_FLEX_COLORS,
  SubscriptionPlanFlexCardRoot,
  SubscriptionPlanFlexCardHeader,
  SubscriptionPlanFlexCardHeaderStatusChip,
  SubscriptionPlanFlexCardContent,
  SubscriptionPlanFlexMetaGrid,
  SubscriptionPlanFlexMetaGridFullRow,
  SubscriptionPlanFlexUsageBarGroup,
  SubscriptionPlanFlexUsageBarTrack,
  SubscriptionPlanFlexUsageBarIncluded,
  SubscriptionPlanFlexUsageBarAvailable,
  SubscriptionPlanFlexUsageBarOverage,
  SubscriptionPlanFlexUsageBarLimitMarker,
  SubscriptionPlanFlexUsageBarLimitLabels,
  SubscriptionPlanFlexUsageBarLimitLabel,
  SubscriptionPlanFlexLegendDot,
  SubscriptionPlanFlexSummaryGrid,
  SubscriptionPlanFlexSummaryTile,
  SubscriptionPlanFlexNextPaymentRoot,
  SubscriptionPlanFlexNextPaymentHeader,
  SubscriptionPlanFlexNextPaymentBody,
  SubscriptionPlanFlexNextPaymentRow,
  SubscriptionPlanFlexFootnote,
} from './styles';

const warningCancelPlanSub =
  'Esta acción cancelara la subscripción del plan. Una vez cancelada no se puede dar vuelta atras';

const UNIT_PRICE_FORMAT_OPTIONS: Intl.NumberFormatOptions = {
  style: 'currency',
  currency: 'USD',
  currencyDisplay: 'code',
  minimumFractionDigits: 2,
  maximumFractionDigits: 3,
};

const formatOrdersCount = (value: number): string =>
  formatCurrencyValue(value, { style: 'decimal' });

const clampPercent = (value: number): number =>
  Math.min(100, Math.max(0, value));

const SubscriptionPlanFlexCard = ({
  name,
  statusType,
  activedDate,
  expiredDate,
  cycleDate,
  contractPlanId,
  gatewayType,
  frecuencyType,
  isTrial = false,
  orders,
  payment,
  actions,
  companyPlanId,
}: SubscriptionPlanFlexCardProps): ReactElement => {
  const { enqueueSnackbar } = useSnackbar();

  const isExpired = useMemo(() => {
    const now = moment();
    const expiration = moment(expiredDate);

    return expiration.isBefore(now, 'day');
  }, [expiredDate]);

  const isCancelled = useMemo(
    () => isCancelledCurrentPlan(statusType),
    [statusType],
  );

  const isSinglePayment = useMemo(
    () => frecuencyType === PaymentFrecuencyTypes.SINGLE,
    [frecuencyType],
  );

  const isEnabledNextPaymentActions = useMemo(
    () => !isExpired && !isTrial && !isCancelled && !isSinglePayment,
    [isCancelled, isExpired, isTrial, isSinglePayment],
  );

  const planNameText = useMemo(
    () => (isTrial ? `${name} (PRUEBA)` : name),
    [isTrial, name],
  );

  const statusText = useMemo(
    () => (isExpired ? 'Vencido' : 'Activo'),
    [isExpired],
  );

  const cycleDateText = useMemo(() => {
    if (!isSinglePayment && cycleDate) {
      const fromText = formatterDate(cycleDate.from, { format: 'DD/MM/YYYY' });
      const tillText = formatterDate(cycleDate.till, { format: 'DD/MM/YYYY' });

      return `${fromText} al ${tillText}`;
    }

    return undefined;
  }, [isSinglePayment, cycleDate]);

  const paymentTypeText = useMemo(() => {
    if (payment?.method && payment.last4Digits) {
      return `${upperCase(payment.method)} terminada en ${payment.last4Digits}`;
    }

    return undefined;
  }, [payment]);

  const usage = useMemo(() => {
    if (isNil(orders)) return undefined;

    const { included, consumed, overage, overageUnitPrice, basePrice } = orders;

    const hasOverage = overage > 0;
    const available = Math.max(0, included - consumed);
    const total = Math.max(consumed, included);

    const includedPercent =
      total > 0 ? clampPercent((included / total) * 100) : 0;
    const overagePercent =
      total > 0 ? clampPercent((overage / total) * 100) : 0;
    const consumedPercent =
      total > 0 ? clampPercent((consumed / total) * 100) : 0;
    const availablePercent =
      total > 0 ? clampPercent((available / total) * 100) : 0;

    const overageTotal = overage * overageUnitPrice;

    return {
      included,
      consumed,
      overage,
      available,
      overageUnitPrice,
      basePrice,
      hasOverage,
      includedPercent,
      overagePercent,
      consumedPercent,
      availablePercent,
      overageTotal,
      estimatedTotal: basePrice + overageTotal,
    };
  }, [orders]);

  const nextPaymentDateText = useMemo(() => {
    const reference = cycleDate?.till ?? expiredDate;

    return formatterDate(reference, { format: 'DD/MM/YYYY' });
  }, [cycleDate, expiredDate]);

  const [openConfirmCancelPlanSubDialog, toggleOpenConfirmCancelPlanSubDialog] =
    useToggle(false);

  const messageCancelPlanSub = useMemo(
    () => `La subscripción al plan ${name} será cancelado`,
    [name],
  );

  const isMPGatweay = useMemo(
    () => gatewayType === PaymentGatewayTypes.MERCADOPAGO,
    [gatewayType],
  );

  const cancelPlanSubRequest = useMemo(
    () =>
      isMPGatweay
        ? cancelMPPaymentPlanSubscription
        : cancelPaypalPaymentPlanSubscription,
    [isMPGatweay],
  );

  const [, loadingCancelPlanSub, , executeCancelPlanSub] = useLazyRequest({
    request: cancelPlanSubRequest,
    withPostSuccess: () => {
      actions?.onCancelSuccess?.();

      const message = `La subscripción al plan ${name} fue cancelada correctamente`;

      enqueueSnackbar(message, { variant: 'success' });

      toggleOpenConfirmCancelPlanSubDialog(false);
    },
    withPostFailure: () => {
      actions?.onCancelFailure?.();

      enqueueSnackbar('No se pudo elimnar el pedido, intente nuevamente', {
        variant: 'error',
      });
    },
  });

  const handleCancelPlanSub = useCallback(() => {
    executeCancelPlanSub(contractPlanId);
  }, [executeCancelPlanSub, contractPlanId]);

  const [openEditPaymentModal, toggleEditPaymentModal] = useToggle(false);

  return (
    <>
      <SubscriptionPlanFlexCardRoot>
        <SubscriptionPlanFlexCardHeader>
          <SubscriptionPlanCardRowInfo
            value={planNameText}
            icon={SubscriptionsIcon}
            textColor="textPrimary"
          />

          <Stack spacing={1} direction="row" alignItems="center">
            <SubscriptionPlanFlexCardHeaderStatusChip
              label={statusText}
              variant="outlined"
              size="medium"
              isExpired={isExpired}
            />

            {isCancelled && !isSinglePayment && (
              <SubscriptionPlanFlexCardHeaderStatusChip
                label="Sin renovación"
                variant="outlined"
                size="medium"
                isExpired={false}
                hasRenewal
              />
            )}

            {isEnabledNextPaymentActions && (
              <MenuActionsDataTable
                resource={{
                  path: PATHS.CONFIG.INTEGRATIONS.BASE,
                }}
                items={{
                  show: { enabled: false },
                  edit: { enabled: false },
                }}
                extraItems={[
                  {
                    enabled: isMPGatweay,
                    label: 'Modificar método de pago',
                    Icon: <EditIcon color="success" />,
                    onClick: () => toggleEditPaymentModal(true),
                    divider: true,
                  },
                  {
                    enabled: true,
                    label: 'Cancelar',
                    Icon: <CancelIcon color="error" />,
                    onClick: () => toggleOpenConfirmCancelPlanSubDialog(true),
                  },
                ]}
                optionsButton={{
                  color: 'inherit',
                  variant: 'contained',
                  size: 'small',
                }}
              />
            )}
          </Stack>
        </SubscriptionPlanFlexCardHeader>

        <SubscriptionPlanFlexCardContent>
          <SubscriptionPlanFlexMetaGrid>
            <SubscriptionPlanCardRowInfo
              description="Fecha de alta:"
              value={formatterDate(activedDate, { format: 'DD/MM/YYYY' })}
              icon={ActiveDateIcon}
              textColor="textSecondary"
            />

            <SubscriptionPlanCardRowInfo
              description="Vencimiento:"
              value={formatterDate(expiredDate, { format: 'DD/MM/YYYY' })}
              icon={ExpiredDateIcon}
              textColor="textSecondary"
            />

            {cycleDateText && (
              <SubscriptionPlanFlexMetaGridFullRow>
                <SubscriptionPlanCardRowInfo
                  description="Ciclo actual:"
                  value={cycleDateText}
                  icon={CyleDateIcon}
                  textColor="textSecondary"
                />
              </SubscriptionPlanFlexMetaGridFullRow>
            )}

            {paymentTypeText && (
              <SubscriptionPlanFlexMetaGridFullRow>
                <SubscriptionPlanCardRowInfo
                  description="Método de pago:"
                  value={paymentTypeText}
                  icon={CreditCardIcon}
                  textColor="textSecondary"
                />
              </SubscriptionPlanFlexMetaGridFullRow>
            )}
          </SubscriptionPlanFlexMetaGrid>

          {usage && (
            <>
              <Stack spacing={1}>
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  spacing={1}
                >
                  <Typography variant="body2" color="text.secondary">
                    Uso de pedidos
                  </Typography>

                  <Typography variant="body2" color="text.secondary">
                    {formatOrdersCount(usage.consumed)} /{' '}
                    {formatOrdersCount(usage.included)} incluidos
                  </Typography>
                </Stack>

                <SubscriptionPlanFlexUsageBarGroup>
                  <SubscriptionPlanFlexUsageBarTrack>
                    <SubscriptionPlanFlexUsageBarIncluded
                      widthPercent={
                        usage.hasOverage
                          ? usage.includedPercent
                          : usage.consumedPercent
                      }
                    />

                    {usage.hasOverage ? (
                      <>
                        <SubscriptionPlanFlexUsageBarOverage
                          widthPercent={usage.overagePercent}
                        />

                        <SubscriptionPlanFlexUsageBarLimitMarker
                          leftPercent={usage.includedPercent}
                        />
                      </>
                    ) : (
                      <SubscriptionPlanFlexUsageBarAvailable
                        widthPercent={usage.availablePercent}
                      />
                    )}
                  </SubscriptionPlanFlexUsageBarTrack>

                  {usage.hasOverage && (
                    <SubscriptionPlanFlexUsageBarLimitLabels>
                      <SubscriptionPlanFlexUsageBarLimitLabel
                        leftPercent={usage.includedPercent}
                      >
                        <Typography variant="caption" color="text.secondary">
                          límite incluido ↑
                        </Typography>
                      </SubscriptionPlanFlexUsageBarLimitLabel>
                    </SubscriptionPlanFlexUsageBarLimitLabels>
                  )}
                </SubscriptionPlanFlexUsageBarGroup>

                <Stack direction="row" spacing={2} flexWrap="wrap">
                  {usage.hasOverage ? (
                    <>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        display="flex"
                        alignItems="center"
                      >
                        <SubscriptionPlanFlexLegendDot
                          dotColor={PLAN_FLEX_COLORS.included}
                        />
                        Incluidos ({formatOrdersCount(usage.included)})
                      </Typography>

                      <Typography
                        variant="caption"
                        color="text.secondary"
                        display="flex"
                        alignItems="center"
                      >
                        <SubscriptionPlanFlexLegendDot
                          dotColor={PLAN_FLEX_COLORS.overage}
                        />
                        Excedente facturable ({formatOrdersCount(usage.overage)}
                        )
                      </Typography>
                    </>
                  ) : (
                    <>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        display="flex"
                        alignItems="center"
                      >
                        <SubscriptionPlanFlexLegendDot
                          dotColor={PLAN_FLEX_COLORS.included}
                        />
                        Consumidos ({formatOrdersCount(usage.consumed)})
                      </Typography>

                      <Typography
                        variant="caption"
                        color="text.secondary"
                        display="flex"
                        alignItems="center"
                      >
                        <SubscriptionPlanFlexLegendDot
                          dotColor={PLAN_FLEX_COLORS.available}
                        />
                        Disponibles ({formatOrdersCount(usage.available)})
                      </Typography>
                    </>
                  )}
                </Stack>
              </Stack>

              <SubscriptionPlanFlexSummaryGrid>
                <SubscriptionPlanFlexSummaryTile>
                  <Typography variant="body2" color="text.secondary">
                    Incluidos en tu plan
                  </Typography>
                  <Typography variant="h5" fontWeight="bold">
                    {formatOrdersCount(usage.included)}
                  </Typography>
                </SubscriptionPlanFlexSummaryTile>

                <SubscriptionPlanFlexSummaryTile>
                  <Typography variant="body2" color="text.secondary">
                    Consumidos
                  </Typography>
                  <Typography variant="h5" fontWeight="bold">
                    {formatOrdersCount(usage.consumed)}
                  </Typography>
                </SubscriptionPlanFlexSummaryTile>

                {usage.hasOverage && (
                  <SubscriptionPlanFlexSummaryTile highlight>
                    <Typography variant="body2" color="text.secondary">
                      Excedente facturable
                    </Typography>
                    <Typography
                      variant="h5"
                      fontWeight="bold"
                      sx={{ color: PLAN_FLEX_COLORS.overage }}
                    >
                      {formatOrdersCount(usage.overage)}
                    </Typography>
                  </SubscriptionPlanFlexSummaryTile>
                )}
              </SubscriptionPlanFlexSummaryGrid>

              {!isSinglePayment && (
                <SubscriptionPlanFlexNextPaymentRoot>
                  <SubscriptionPlanFlexNextPaymentHeader>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <FontAwesomeIcon
                        icon={NextPaymentIcon}
                        color={PLAN_FLEX_COLORS.included}
                      />
                      <Typography variant="body2" fontWeight="bold">
                        Próximo pago estimado
                      </Typography>
                    </Stack>

                    <Typography variant="caption" color="text.secondary">
                      al {nextPaymentDateText}
                    </Typography>
                  </SubscriptionPlanFlexNextPaymentHeader>

                  <SubscriptionPlanFlexNextPaymentBody>
                    <SubscriptionPlanFlexNextPaymentRow>
                      <Typography variant="body2" color="text.secondary">
                        Base del plan {name}
                      </Typography>
                      <Typography variant="body2" fontWeight="bold">
                        {formatCurrencyValue(usage.basePrice)}
                      </Typography>
                    </SubscriptionPlanFlexNextPaymentRow>

                    {usage.hasOverage && (
                      <SubscriptionPlanFlexNextPaymentRow>
                        <Typography variant="body2" color="text.secondary">
                          Excedentes{' '}
                          <Typography
                            component="span"
                            variant="body2"
                            color="text.disabled"
                          >
                            · {formatOrdersCount(usage.overage)} ×{' '}
                            {formatCurrencyValue(
                              usage.overageUnitPrice,
                              UNIT_PRICE_FORMAT_OPTIONS,
                            )}
                          </Typography>
                        </Typography>
                        <Typography
                          variant="body2"
                          fontWeight="bold"
                          sx={{ color: PLAN_FLEX_COLORS.overage }}
                        >
                          + {formatCurrencyValue(usage.overageTotal)}
                        </Typography>
                      </SubscriptionPlanFlexNextPaymentRow>
                    )}

                    <Divider />

                    <SubscriptionPlanFlexNextPaymentRow>
                      <Typography variant="body1" fontWeight="bold">
                        Total estimado
                      </Typography>
                      <Typography
                        variant="h6"
                        fontWeight="bold"
                        sx={{ color: PLAN_FLEX_COLORS.included }}
                      >
                        {formatCurrencyValue(usage.estimatedTotal)}
                      </Typography>
                    </SubscriptionPlanFlexNextPaymentRow>
                  </SubscriptionPlanFlexNextPaymentBody>
                </SubscriptionPlanFlexNextPaymentRoot>
              )}

              <SubscriptionPlanFlexFootnote>
                <FontAwesomeIcon
                  icon={InfoIcon}
                  color={PLAN_FLEX_COLORS.included}
                />
                <Typography variant="caption">
                  El total incluye la base del plan más los pedidos excedentes.
                  Cada pedido por encima de los{' '}
                  <Typography
                    component="span"
                    variant="caption"
                    fontWeight="bold"
                  >
                    {formatOrdersCount(usage.included)} incluidos
                  </Typography>{' '}
                  se factura a{' '}
                  <Typography
                    component="span"
                    variant="caption"
                    fontWeight="bold"
                  >
                    {formatCurrencyValue(
                      usage.overageUnitPrice,
                      UNIT_PRICE_FORMAT_OPTIONS,
                    )}
                  </Typography>
                  . El monto puede variar según los pedidos que registres hasta
                  el cierre del ciclo.
                </Typography>
              </SubscriptionPlanFlexFootnote>
            </>
          )}
        </SubscriptionPlanFlexCardContent>
      </SubscriptionPlanFlexCardRoot>

      <DialogConfimation
        open={openConfirmCancelPlanSubDialog}
        description={messageCancelPlanSub}
        warningText={warningCancelPlanSub}
        onConfirm={handleCancelPlanSub}
        onCancel={() => toggleOpenConfirmCancelPlanSubDialog(false)}
        loadingConfirm={loadingCancelPlanSub}
      />

      <SubscriptionEditPaymentModal
        companyPlanId={companyPlanId}
        open={openEditPaymentModal}
        onClose={() => toggleEditPaymentModal(false)}
      />
    </>
  );
};

export default SubscriptionPlanFlexCard;
